/**
 * useActivityFeed Hook
 *
 * React hook for fetching and managing activity feed entries.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { ActivityFeedEntry } from '../types';
import {
  fetchActivityFeed,
  fetchDashboardActivityFeed,
  groupActivitiesByMonth,
  checkAndCreateSSWMilestones,
  checkAndCreateSSWChanges,
} from '../services/activityFeedService';
import { usePregnancyStore } from '@/store/pregnancyStore';

interface UseActivityFeedOptions {
  dashboardMode?: boolean;
  limit?: number;
  autoFetch?: boolean;
}

interface UseActivityFeedReturn {
  activities: ActivityFeedEntry[];
  groupedActivities: Map<string, ActivityFeedEntry[]>;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useActivityFeed(
  options: UseActivityFeedOptions = {}
): UseActivityFeedReturn {
  const { dashboardMode = false, limit = 10, autoFetch = true } = options;

  const [activities, setActivities] = useState<ActivityFeedEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const currentPregnancy = usePregnancyStore((state) => state.currentPregnancy);

  const fetchData = useCallback(
    async (isRefresh = false) => {
      setIsLoading(true);
      setError(null);

      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user) {
          setError('Nicht eingeloggt');
          setActivities([]);
          return;
        }

        // Check and create any missing SSW entries before fetching
        if (currentPregnancy?.id && !currentPregnancy.completion_type) {
          // Create milestones (SSW 12, 20, 27, 37, 40)
          await checkAndCreateSSWMilestones(currentPregnancy.id);
          // Create SSW change entries (every week with baby development info)
          await checkAndCreateSSWChanges(currentPregnancy.id);
        }

        let data: ActivityFeedEntry[];

        if (dashboardMode) {
          data = await fetchDashboardActivityFeed(user.id);
          setHasMore(false); // Dashboard mode doesn't support pagination
        } else {
          const currentOffset = isRefresh ? 0 : offset;
          data = await fetchActivityFeed(user.id, {
            limit,
            offset: currentOffset,
            enabledTypesOnly: true,
          });

          if (isRefresh) {
            setActivities(data);
            setOffset(limit);
          } else {
            setActivities((prev) =>
              currentOffset === 0 ? data : [...prev, ...data]
            );
            setOffset(currentOffset + limit);
          }

          setHasMore(data.length === limit);
          return;
        }

        setActivities(data);
      } catch (err) {
        console.error('Error fetching activity feed:', err);
        setError('Fehler beim Laden der Aktivitäten');
      } finally {
        setIsLoading(false);
      }
    },
    [dashboardMode, limit, offset, currentPregnancy?.id, currentPregnancy?.completion_type]
  );

  // Initial fetch
  useEffect(() => {
    if (autoFetch) {
      fetchData(true);
    }
  }, [autoFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;
    await fetchData(false);
  }, [fetchData, hasMore, isLoading]);

  const refresh = useCallback(async () => {
    setOffset(0);
    await fetchData(true);
  }, [fetchData]);

  const groupedActivities = groupActivitiesByMonth(activities);

  return {
    activities,
    groupedActivities,
    isLoading,
    error,
    hasMore,
    loadMore,
    refresh,
  };
}

export default useActivityFeed;
